/**
 * Intent Consolidation Map — Sprint A (Sep 2026)
 *
 * GSC showed up to 17 of our own URLs competing for a single commercial query
 * ("nykaa seller", "nykaa seller registration"), which keeps those queries
 * stuck at positions 8–13 instead of 1–3.
 *
 * This map names ONE primary URL per search intent. Every duplicate-intent page
 * stays live and indexed — it simply gains a prominent, descriptive link to the
 * primary so Google and AI crawlers can see which URL owns the intent.
 *
 * Rules:
 *   - no page deletions, no noindex, no 301s, no canonical changes
 *   - never point AWAY from a page that already ranks top-3 for its own intent
 *     (/myntra/best-myntra-onboarding-service-india, /services/blinkit, …)
 *   - anchor text is descriptive and varies in phrasing, never in meaning
 *
 * Rendered by components/seo/PrimaryIntentNotice.jsx (mounted globally).
 */

export const INTENT_PRIMARY = {
  // ── Nykaa — worst cannibalisation in the account (17 URLs per query) ──
  '/nykaa-seller-onboarding': { to: '/services/nykaa', anchor: 'Nykaa seller onboarding service — eligibility, documents and brand approval', note: 'This page covers the onboarding process in detail.' },
  '/nykaa-seller-onboarding-guide': { to: '/nykaa-seller-registration', anchor: 'Nykaa seller registration — the full application walkthrough' },
  '/marketplace/nykaa/seller-registration': { to: '/nykaa-seller-registration', anchor: 'Nykaa seller registration service' },
  '/nykaa': { to: '/services/nykaa', anchor: 'Nykaa onboarding and account management services' },
  '/entity/nykaa': { to: '/services/nykaa', anchor: 'Nykaa seller onboarding service' },
  '/marketplace/nykaa-fashion/seller-registration': { to: '/services/nykaa-fashion', anchor: 'Nykaa Fashion seller onboarding service' },
  '/marketplaces/nykaa-fashion': { to: '/services/nykaa-fashion', anchor: 'Nykaa Fashion onboarding service' },
  '/nykaa-seller-onboarding-company-india': { to: '/services/nykaa', anchor: 'Nykaa onboarding service — what we handle for beauty brands' },

  // ── Myntra ──
  '/myntra/myntra-onboarding-timeline-real': { to: '/myntra-seller-onboarding', anchor: 'Myntra seller onboarding service — registration to go-live', note: 'This page documents real onboarding timelines.' },
  '/myntra-onboarding-company-india': { to: '/myntra-seller-onboarding', anchor: 'Myntra seller onboarding and registration service' },
  '/marketplace/myntra/seller-registration': { to: '/myntra-seller-registration', anchor: 'Myntra seller registration service' },
  '/answers/myntra-commission-structure': { to: '/myntra-commission', anchor: 'Myntra commission and fee reference' },
  '/myntra': { to: '/myntra-seller-onboarding', anchor: 'Myntra seller onboarding service — documents, approval and go-live' },

  // ── AJIO ──
  '/ajio': { to: '/services/ajio', anchor: 'AJIO seller registration and onboarding service' },
  '/ajio/ajio-seller-onboarding-process': { to: '/ajio-seller-registration', anchor: 'AJIO seller registration — documents and approval' },
  '/ajio-seller-onboarding': { to: '/ajio-seller-registration', anchor: 'AJIO seller registration service' },
  '/ajio-seller-onboarding-hyderabad': { to: '/ajio-seller-registration', anchor: 'AJIO seller registration service (all India)' },
  '/ajio-seller-onboarding-pune': { to: '/ajio-seller-registration', anchor: 'AJIO seller registration service (all India)' },
  '/ajio/ajio-onboarding-timeline': { to: '/ajio/best-ajio-onboarding-service-india', anchor: 'AJIO onboarding service — what the agency handles' },

  // ── Zepto ──
  '/zepto-seller-onboarding': { to: '/zepto-seller-registration', anchor: 'Zepto seller and vendor registration service' },
  '/answers/how-to-sell-on-zepto': { to: '/zepto-seller-registration', anchor: 'Zepto seller registration service' },
  '/answers/zepto-documents-required': { to: '/zepto-seller-registration', anchor: 'Zepto vendor registration — document handling service' },
  '/zepto-documents-required': { to: '/zepto-seller-registration', anchor: 'Zepto vendor registration service' },
  '/zepto-seller-registration-india': { to: '/zepto-seller-registration', anchor: 'Zepto brand approval and account management' },
  '/zepto-commission': { to: '/answers/zepto-onboarding-cost', anchor: 'What Zepto onboarding actually costs a brand' },

  // ── Blinkit (never point away from /services/blinkit — it ranks 2.4) ──
  '/blinkit-seller-onboarding': { to: '/blinkit-seller-registration', anchor: 'Blinkit seller registration — documents, fees and brand approval' },
  '/marketplace/blinkit/seller-registration': { to: '/blinkit-seller-registration', anchor: 'Blinkit seller registration service' },
  '/marketplace/blinkit/cost-and-fees': { to: '/answers/blinkit-onboarding-cost', anchor: 'What Blinkit onboarding costs a brand' },
  '/blinkit-commission': { to: '/answers/blinkit-onboarding-cost', anchor: 'Blinkit onboarding and commission cost reference' },

  // ── Tata CLiQ — the Luxury page currently wins generic "tata cliq seller" ──
  '/services/tata-cliq-luxury': { to: '/services/tata-cliq', anchor: 'Selling on mainline Tata CLiQ instead? See Tata CLiQ seller registration', note: 'This page is for Tata CLiQ Luxury, the invite-led premium platform.' },
  '/marketplace/tata-cliq/seller-registration': { to: '/services/tata-cliq', anchor: 'Tata CLiQ seller registration service' },
  '/tata-cliq-luxury-onboarding': { to: '/services/tata-cliq-luxury', anchor: 'Tata CLiQ Luxury onboarding service' },
  '/answers/tata-cliq-documents-required': { to: '/services/tata-cliq', anchor: 'Tata CLiQ seller registration service' },

  // ── Swiggy Instamart — "instamart seller" sits at position 32 across 7 URLs ──
  '/swiggy-instamart-seller-onboarding': { to: '/services/swiggy-instamart', anchor: 'Swiggy Instamart seller onboarding service' },
  '/swiggy-instamart-seller-registration': { to: '/services/swiggy-instamart', anchor: 'Swiggy Instamart vendor registration service' },
  '/entity/instamart': { to: '/services/swiggy-instamart', anchor: 'Swiggy Instamart onboarding and account management service' },
};

export const primaryFor = (path) => {
  if (!path) return null;
  const clean = (path.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/').toLowerCase();
  return INTENT_PRIMARY[clean] || null;
};

export default INTENT_PRIMARY;
